import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FilePdf, Truck, IdentificationCard, ClipboardText, Package, Trash, PencilSimple } from "@phosphor-icons/react";
import { usePerms } from "@/lib/perms";
import EditRecoleccionModal from "@/components/EditRecoleccionModal";

const ACCENT = "#10B981";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric", month: "long", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

export default function RecoleccionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { has } = usePerms();
  const canDelete = has("appointments:delete");
  const canEdit = has("appointments:edit");
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/recolecciones/${id}`);
        setR(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!r) return;
    let revokeUrl = null;
    (async () => {
      try {
        const token = localStorage.getItem("rcn_token");
        const res = await fetch(`${API}/recolecciones/${r.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        revokeUrl = URL.createObjectURL(blob);
        setPdfUrl(revokeUrl);
      } catch (err) {
        console.debug("[RecoleccionDetail] preview load failed", err);
      }
    })();
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [r]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/recolecciones/${r.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Recoleccion_${r.folio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar la recolección ${r.folio}?`)) return;
    try {
      await api.delete(`/recolecciones/${r.id}`);
      toast.success("Recolección eliminada");
      nav("/recolecciones");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="p-8 text-center text-white/50">Cargando…</div>;
  if (!r) return <div className="p-8 text-center text-white/50">Recolección no encontrada.</div>;

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/recolecciones" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white mb-2">
            <ArrowLeft size={13} /> Volver
          </Link>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] mb-1" style={{ color: ACCENT }}>
            Recolección
          </div>
          <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">
            <Truck size={32} weight="duotone" style={{ color: ACCENT }} />
            <span data-testid="rec-detail-folio" style={{ color: ACCENT }}>{r.folio}</span>
          </h1>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <button
              data-testid="rec-detail-edit"
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm font-semibold hover:brightness-110"
              style={{ borderColor: ACCENT, color: ACCENT, backgroundColor: "rgba(16,185,129,0.08)" }}
            >
              <PencilSimple size={16} weight="fill" /> Editar
            </button>
          )}
          <button
            data-testid="rec-detail-download"
            onClick={downloadPdf}
            className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-white text-sm font-semibold hover:brightness-110"
            style={{ backgroundColor: ACCENT }}
          >
            <FilePdf size={16} weight="fill" /> Descargar PDF
          </button>
          {canDelete && (
            <button
              onClick={remove}
              data-testid="rec-detail-delete"
              className="flex items-center gap-2 px-3 py-2.5 rounded-sm border border-white/10 text-white/60 hover:text-[#FF3B30] hover:border-[#FF3B30]/40"
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <Section icon={Package} title="Datos generales">
            <KV label="Fecha y hora" value={fmt(r.appointment_datetime)} />
            <KV label="Cliente / Proveedor" value={r.cliente_name} />
            <KV label="Tipo de unidad" value={r.unit_type_name} />
            <KV label="Creado por" value={(r.created_by || "—").toUpperCase()} />
          </Section>

          <Section icon={IdentificationCard} title="Recolector">
            <KV label="Persona" value={r.persona_recolecta || "—"} />
            <KV label="Placas" mono value={r.placas || "—"} />
          </Section>

          <Section icon={ClipboardText} title={`Pedidos / RMA · ${(r.pedidos || []).length}`}>
            {(r.pedidos || []).length === 0 ? (
              <div className="text-sm text-white/40 italic">Sin pedidos capturados.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {r.pedidos.map((p, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 rounded-full text-xs font-mono-data border"
                    style={{ borderColor: ACCENT, color: ACCENT, backgroundColor: "rgba(16,185,129,0.08)" }}
                  >
                    {p}
                  </span>
                ))}
              </div>
            )}
          </Section>

          {r.notes && (
            <Section icon={ClipboardText} title="Notas">
              <div className="text-sm text-white/80 whitespace-pre-wrap">{r.notes}</div>
            </Section>
          )}
        </div>

        <div className="bg-[#111111] border border-white/10 rounded-sm overflow-hidden sticky top-6" style={{ minHeight: "70vh" }}>
          <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50">Vista previa del pase</div>
            <div className="text-[10px] font-mono-data" style={{ color: ACCENT }}>PDF</div>
          </div>
          {pdfUrl ? (
            <iframe
              title="Pase Recolección PDF"
              src={pdfUrl}
              className="w-full"
              style={{ height: "80vh", backgroundColor: "#1a1a1a" }}
            />
          ) : (
            <div className="p-8 text-center text-white/40 text-sm">Generando vista previa…</div>
          )}
        </div>
      </div>

      {editing && (
        <EditRecoleccionModal
          rec={r}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setR(updated);
            setPdfUrl(null); // forzar regeneración del preview
          }}
        />
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#111111] border border-white/10 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} style={{ color: ACCENT }} weight="duotone" />
        <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/50">{title}</div>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-white/50 text-xs uppercase font-mono-data tracking-widest">{label}</span>
      <span className={`text-white ${mono ? "font-mono-data font-bold" : ""}`}>{value}</span>
    </div>
  );
}
